<template>
  <div class="message-template-edit-content">
    <ValidationObserver ref="observerRef" v-slot="{ dirty }" tag="form">
      <hr />
      <div class="rack compress nudge-together">
        <SnippetTrigger
          label="First Name"
          data-cy="first-name-snippet"
          @click.native="mergeInput('{{ first_name }}')"
        />
        <SnippetTrigger
          label="Last Name"
          data-cy="last-name-snippet"
          @click.native="mergeInput('{{ last_name }}')"
        />
        <SnippetTrigger
          :label="dueDateSnippetLabel"
          data-cy="due-date-snippet"
          @click.native="mergeInput('{{ due_date }}')"
        />
        <SnippetTrigger
          label="Days Past Due"
          data-cy="days-past-due-snippet"
          @click.native="mergeInput('{{ days_past_due }}')"
        />
        <SnippetTrigger
          label="Days Until Due"
          data-cy="days-until-due-snippet"
          @click.native="mergeInput('{{ days_until_due }}')"
        />
        <SnippetTrigger
          label="Amount"
          data-cy="due-date-snippet"
          @click.native="mergeInput('{{ amount_due }}')"
        />
        <SnippetTrigger
          v-if="allowAutopaySnippet"
          label="Cadence"
          data-cy="payment-link-snippet"
          @click.native="mergeInput('{{ cadence }}')"
        />
        <SnippetTrigger
          v-if="allowAutopaySnippet"
          label="Payment Method"
          data-cy="payment-link-snippet"
          @click.native="mergeInput('{{ payment_method }}')"
        />
        <SnippetTrigger
          v-if="allowPaymentLinkSnippet"
          label="Payment Link"
          data-cy="payment-link-snippet"
          @click.native="mergeInput('{{ payment_link }}')"
        />
        <SnippetTrigger
          label="Location"
          data-cy="location-snippet"
          @click.native="mergeInput('{{ location }}')"
        />
        <SnippetTrigger
          label="Account Number"
          data-cy="account-number-snippet"
          @click.native="mergeInput('{{ account_number }}')"
        />
        <template v-if="!isPropertyPlatform">
          <SnippetTrigger
            label="Year"
            data-cy="vehicle-year-snippet"
            @click.native="mergeInput('{{ vehicle_year }}')"
          />
          <SnippetTrigger
            label="Make"
            data-cy="vehicle-make-snippet"
            @click.native="mergeInput('{{ vehicle_make }}')"
          />
          <SnippetTrigger
            label="Model"
            data-cy="vehicle-model-snippet"
            @click.native="mergeInput('{{ vehicle_model }}')"
          />
        </template>
      </div>
      <b-row>
        <b-col cols="12">
          <ValidationProvider name="sms_body">
            <b-textarea
              :key="smsKey"
              v-model="messageTemplate.sms_body"
              no-auto-shrink
              :maxlength="300"
              data-cy="textfield"
              @keyup="setLength"
              @change="templateBodyDraft(dirty)"
            />
          </ValidationProvider>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="length-error">
          <span :class="bodyCharLength">
            {{ bodyLength }}
          </span>
        </b-col>
      </b-row>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'
import { isPropertyPlatform } from '@/utils/platformLabels.js'

import SnippetTrigger from '@/components/SnippetTrigger/SnippetTrigger/SnippetTrigger.vue'

export interface MessageTemplate {
  sms_body: string
  type: string
}

@Component({
  components: {
    SnippetTrigger
  },
  computed: {
    ...mapGetters(['currentMerchant'])
  }
})
export default class MessageTemplateEdit extends Vue {
  @Prop({ required: true }) messageTemplate!: MessageTemplate

  currentMerchant!: Record<string, any>

  get isPropertyPlatform(): boolean {
    return isPropertyPlatform(null, this.currentMerchant)
  }

  bodyLength = 0
  mergeField = null
  smsKey = 0

  @Watch('messageTemplate')
  setLength() {
    this.bodyLength = this.messageTemplate.sms_body?.length
  }

  allowPaymentLink(type) {
    // Following types generate payment link in template - prevent double links
    const exempt = [
      'PAYMENT_FAILED',
      'PAYMENTREQUEST',
      'SCHEDULED',
      'WELCOME',
      'REGISTER'
    ]

    return !exempt.includes(type)
  }

  mergeInput(value) {
    this.messageTemplate.sms_body = this.messageTemplate.sms_body + value
    this.smsKey++
    // Emit event to notify parent of changes
    this.$emit('templateBodyDraft', true)
  }

  templateBodyDraft(isDirty) {
    this.$emit('templateBodyDraft', isDirty)
  }

  get allowAutopaySnippet(): boolean {
    return (
      this.messageTemplate.type === 'AUTOPAY' ||
      this.messageTemplate.type === 'AUTOPAY_MERCHANT'
    )
  }

  get allowPaymentLinkSnippet(): boolean {
    // Following types generate payment link in template render - prevent double links
    const exempt = ['SCHEDULED', 'PAYMENTREQUEST', 'PAYMENT_FAILED']

    return !exempt.includes(this.messageTemplate.type)
  }

  get bodyCharLength(): string {
    return this.bodyLength > 290 ? 'text-danger' : ''
  }

  get dueDateSnippetLabel(): string {
    return this.messageTemplate.type === 'AUTOPAY' ? 'Next Date' : 'Due Date'
  }
}
</script>

<style lang="scss" scoped>
.message-template-edit-content {
  margin-top: 18px;

  p {
    height: 23px;
    margin-left: 13.5px;

    color: gray;
    font-size: 20px;
    line-height: 1;
    letter-spacing: 0;

    background-color: $background;
    opacity: 1;
  }

  textarea {
    width: 100%;
    height: 184px;
    padding: 10.5px 13.5px;

    color: gray;
    font-size: 20px;
    font-family: $body-font-family;
    line-height: 30px;
    letter-spacing: 0;

    border: 1px solid $light-gray;
    border-radius: 6px;

    resize: none;
  }

  div.col {
    display: flex;

    color: gray;
  }

  .invoice-url-placeholder {
    margin-top: 12px;
  }

  .length-error {
    display: flex;
    justify-content: flex-end;
  }
}

.rack.compress.nudge-together {
  margin-top: 25px;

  font-size: 0.9rem;
}

.rack.compress.nudge-together > div {
  padding-left: 0;
}
</style>
